/**
 * HttpRepository — fetch + envelope unwrap + Zod parse (plan §1.1 · naming §3.5).
 *
 * search() prefetches the universe once per asOf and runs core searchSecurities
 * (plan §6.6 · DatasetRepository port) — never /search per keystroke.
 */
import { z } from 'zod';

import {
  API_LIMIT_MAX,
  AreaDetailSchema,
  AreaListPayloadSchema,
  BookPayloadSchema,
  DashboardPayloadSchema,
  DatasetMetaSchema,
  DiversificationPayloadSchema,
  formatCompareSet,
  MarketSnapshotPayloadSchema,
  PeersPayloadSchema,
  PriceSeriesSchema,
  SearchQuerySchema,
  SecurityProfileSchema,
  SecuritySchema,
  SectorDetailSchema,
  type AreaId,
  type AreaQuery,
  type BenchmarkOhlcQuery,
  type DashboardQuery,
  type DatasetMeta,
  type SearchQuery,
  type SectorId,
  type SectorQuery,
  type Security,
  type SeriesQuery,
  type Ticker,
} from '@meridian/contracts';
import {
  OhlcUnavailableError,
  searchSecurities,
  type DatasetRepository,
  type DatasetResource,
} from '@meridian/core';

import { buildQuery, httpGetJson } from './http-get';

export interface HttpRepositoryOptions {
  readonly baseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

const listSchema = z.array(SecuritySchema);

type GetJson = <T>(
  path: string,
  schema: z.ZodType<T>,
  notFound?: { readonly resource: DatasetResource; readonly id: string },
) => Promise<T>;

interface PendingUniverse {
  readonly asOf: string;
  readonly rows: Promise<readonly Security[]>;
}

function pendingAfterSettlement(
  current: PendingUniverse | undefined,
  rows: Promise<readonly Security[]>,
): PendingUniverse | undefined {
  return current?.rows === rows ? undefined : current;
}

function createUniverseCache(
  get: GetJson,
  getMeta: () => Promise<DatasetMeta>,
): () => Promise<readonly Security[]> {
  let cache: { readonly asOf: string; readonly rows: readonly Security[] } | undefined;
  const pendingState: { value: PendingUniverse | undefined } = { value: undefined };
  return async () => {
    const meta = await getMeta();
    const asOf = meta.asOf ?? '';
    if (cache?.asOf === asOf) return cache.rows;
    if (pendingState.value?.asOf === asOf) return pendingState.value.rows;
    const size = meta.universeSize > 0 ? meta.universeSize : API_LIMIT_MAX;
    const limit = Math.min(size, API_LIMIT_MAX);
    const rows = get(
      `/securities${buildQuery({ window: '1Y', index: 'ALL', sort: 'ticker', dir: 'asc', limit })}`,
      listSchema,
    ).then((value) => {
      cache = { asOf, rows: value };
      return value;
    });
    pendingState.value = { asOf, rows };
    try {
      return await rows;
    } finally {
      pendingState.value = pendingAfterSettlement(pendingState.value, rows);
    }
  };
}

function createMetaLoader(get: GetJson): {
  readonly getMeta: () => Promise<DatasetMeta>;
  readonly refreshMeta: () => Promise<DatasetMeta>;
  readonly loadUniverse: () => Promise<readonly Security[]>;
} {
  const getMeta = (): Promise<DatasetMeta> => get('/meta', DatasetMetaSchema);
  const loadUniverse = createUniverseCache(get, getMeta);
  return { getMeta, refreshMeta: getMeta, loadUniverse };
}

function compareQuery(compare: DashboardQuery['compare']): string | undefined {
  return compare === undefined ? undefined : formatCompareSet(compare);
}

function areaRoutes(get: GetJson) {
  return {
    getAreas: (query: AreaQuery) =>
      get(
        `/areas${buildQuery({
          window: query.window,
          index: query.index,
          taxonomy: query.taxonomy,
          book: query.book,
          compare: compareQuery(query.compare),
        })}`,
        AreaListPayloadSchema,
      ),
    getArea: (id: AreaId, query: AreaQuery) =>
      get(
        `/areas/${encodeURIComponent(id)}${buildQuery({
          window: query.window,
          index: query.index,
          taxonomy: query.taxonomy,
          book: query.book,
          compare: compareQuery(query.compare),
        })}`,
        AreaDetailSchema,
        { resource: 'area', id },
      ),
  };
}

/**
 * The four ticker-scoped reads, grouped the way `areaRoutes` is: they share a
 * path prefix, a query shape and a not-found resource, so a change to any of
 * those should be one edit rather than four.
 */
function securityRoutes(get: GetJson) {
  const at = (ticker: Ticker, suffix: string, query: SeriesQuery): string =>
    `/securities/${encodeURIComponent(ticker)}${suffix}${buildQuery({ window: query.window })}`;
  return {
    getSecurity: (ticker: Ticker, query: SeriesQuery) =>
      get(at(ticker, '', query), SecurityProfileSchema, { resource: 'security', id: ticker }),
    getSeries: (ticker: Ticker, query: SeriesQuery) =>
      get(at(ticker, '/series', query), PriceSeriesSchema, { resource: 'security', id: ticker }),
    getPeers: (ticker: Ticker, query: SeriesQuery) =>
      get(at(ticker, '/peers', query), PeersPayloadSchema, { resource: 'security', id: ticker }),
    getDiversifiers: (ticker: Ticker, query: SeriesQuery) =>
      get(at(ticker, '/diversifiers', query), DiversificationPayloadSchema, {
        resource: 'security',
        id: ticker,
      }),
  };
}

function buildRepository(get: GetJson) {
  const metaLoader = createMetaLoader(get);
  return {
    getMeta: metaLoader.refreshMeta,
    getDashboard: (query: DashboardQuery) =>
      get(
        `/dashboard${buildQuery({
          window: query.window,
          index: query.index,
          book: query.book,
          compare: compareQuery(query.compare),
        })}`,
        DashboardPayloadSchema,
      ),
    getMarketSnapshot: () => get('/market-snapshot', MarketSnapshotPayloadSchema),
    getBenchmarkSeries: (query: SeriesQuery) =>
      get(`/benchmark/series${buildQuery({ window: query.window })}`, PriceSeriesSchema),
    /* Subject GET is Stage 4; benchmark GET is Stage 6. A silent empty
       series would look like a real 200, so both reject the port error. */
    getOhlcSeries: (_ticker: Ticker, _query: SeriesQuery) =>
      Promise.reject(new OhlcUnavailableError('getOhlcSeries')),
    getBenchmarkOhlcSeries: (_query: BenchmarkOhlcQuery) =>
      Promise.reject(new OhlcUnavailableError('getBenchmarkOhlcSeries')),
    /* No query: ownership is not a function of window or index, so the payload
       is cacheable per session rather than per filter combination. */
    getBook: () => get('/book', BookPayloadSchema),
    getSector: (id: SectorId, query: SectorQuery) =>
      get(
        `/sectors/${encodeURIComponent(id)}${buildQuery({
          window: query.window,
          index: query.index,
          book: query.book,
          compare: compareQuery(query.compare),
        })}`,
        SectorDetailSchema,
        { resource: 'sector', id },
      ),
    ...areaRoutes(get),
    ...securityRoutes(get),
    search: async (query: SearchQuery) => {
      const parsed = SearchQuerySchema.parse(query);
      const universe = await metaLoader.loadUniverse();
      return searchSecurities(universe, parsed.q, parsed.limit);
    },
  } satisfies DatasetRepository;
}

export function createHttpRepository(options: HttpRepositoryOptions): DatasetRepository {
  const base = options.baseUrl.replace(/\/$/, '');
  const fetchImpl = options.fetchImpl ?? fetch;
  const get: GetJson = (path, schema, notFound) =>
    httpGetJson(fetchImpl, `${base}${path}`, schema, notFound);
  return buildRepository(get);
}
